import API from '../config/api';
import React, { useState, useEffect, useRef } from 'react';

import axios from 'axios';
import { toast } from 'react-toastify';

import {
  Container,
  Row,
  Col,
  Form,
  Card,
  Button,
  Badge,
  InputGroup
} from 'react-bootstrap';
import { FaEye } from 'react-icons/fa';

import { Link, useLocation } from 'react-router-dom';

import CustomNavbar from '../components/CustomNavbar';
import QuickViewModal from '../components/QuickViewModal';
import StarRating from '../components/product/StarRating';
import { useProductRatings } from '../hooks/useProductRatings';

/* How long the wishlist button celebrates for. Must match the longest
   animation on the burst below, or the hearts vanish mid-flight. */
const BURST_MS = 900;

/* The hearts that spray out of the button. Fixed rather than random so the
   burst reads as composed instead of scattered — angle in degrees measured
   from the button centre, distance in px, and the size each one flies at. */
const BURST_HEARTS = [
  { char: '❤️', a: -90, d: 48, s: 1 },
  { char: '💗', a: -138, d: 42, s: 0.8 },
  { char: '💖', a: -42, d: 44, s: 0.85 },
  { char: '🤍', a: -170, d: 34, s: 0.68 },
  { char: '💝', a: -10, d: 36, s: 0.7 },
  { char: '❤️', a: -114, d: 30, s: 0.6 },
  { char: '💗', a: -66, d: 32, s: 0.58 }
];

/* Temple offerings drifting up through the page backdrop */
const BG_FLOATS = [
  { char: '🪔', left: '6%', size: '1.9rem', delay: '0s', duration: '26s' },
  { char: '🌸', left: '17%', size: '1.5rem', delay: '6s', duration: '31s' },
  { char: 'ॐ', left: '28%', size: '1.7rem', delay: '12s', duration: '28s' },
  { char: '🔔', left: '39%', size: '1.4rem', delay: '3s', duration: '34s' },
  { char: '🪷', left: '50%', size: '1.8rem', delay: '17s', duration: '29s' },
  { char: '🪔', left: '61%', size: '1.5rem', delay: '9s', duration: '32s' },
  { char: '🌼', left: '72%', size: '1.6rem', delay: '21s', duration: '27s' },
  { char: 'ॐ', left: '83%', size: '1.4rem', delay: '14s', duration: '35s' },
  { char: '🌸', left: '92%', size: '1.7rem', delay: '5s', duration: '30s' },
  { char: '🪷', left: '35%', size: '1.3rem', delay: '24s', duration: '33s' },
  { char: '🔔', left: '66%', size: '1.3rem', delay: '19s', duration: '36s' }
];

/* Deterministic scatter — no Math.random so the layout is stable across renders */
const BG_SPARKS = Array.from({ length: 18 }, (_, i) => ({
  left: `${((i * 37) % 96) + 2}%`,
  top: `${((i * 53) % 90) + 4}%`,
  size: `${3 + (i % 3)}px`,
  delay: `${(i * 0.63).toFixed(2)}s`,
  duration: `${(3.2 + (i % 5) * 0.85).toFixed(2)}s`
}));

/* Fixed, full-viewport animated backdrop for this page. Sits at z-index -1 so
   it paints behind the page content (and over the app-wide GlobalBackground),
   and unmounts with the page. Everything moves via transform/opacity only. */
function ProductsBackdrop() {
  return (
    <div className="pp-bg" aria-hidden="true">
      <span className="pp-bg-veil" />
      <span className="pp-aurora" />

      {/* Slowly counter-rotating mandala rings */}
      <span className="pp-mandala pp-mandala-1" />
      <span className="pp-mandala pp-mandala-2" />

      {/* Sweeping shafts of temple light */}
      <span className="pp-ray pp-ray-1" />
      <span className="pp-ray pp-ray-2" />
      <span className="pp-ray pp-ray-3" />

      {BG_SPARKS.map((spark, i) => (
        <span
          key={`spark-${i}`}
          className="pp-spark"
          style={{
            left: spark.left,
            top: spark.top,
            width: spark.size,
            height: spark.size,
            animationDelay: spark.delay,
            animationDuration: spark.duration
          }}
        />
      ))}

      {BG_FLOATS.map((item, i) => (
        <span
          key={`float-${i}`}
          /* every other one is dropped on phones — see .pp-float-extra */
          className={`pp-float${i % 2 ? ' pp-float-extra' : ''}`}
          style={{
            left: item.left,
            fontSize: item.size,
            animationDelay: item.delay,
            animationDuration: item.duration
          }}
        >
          {item.char}
        </span>
      ))}
    </div>
  );
}

/* Divine dropdown — replaces the native <select> so the option list can be
   styled and animated (browsers do not allow styling native <option> lists). */
function DivineSelect({ value, onChange, options, ariaLabel }) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const boxRef = useRef(null);
  const menuRef = useRef(null);

  const selectedIndex = Math.max(
    0,
    options.findIndex((option) => option.value === value)
  );
  const selected = options[selectedIndex];

  /* Close on outside click / Escape */
  useEffect(() => {
    if (!open) return undefined;

    const handlePointer = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };

    document.addEventListener('mousedown', handlePointer);

    return () => document.removeEventListener('mousedown', handlePointer);
  }, [open]);

  /* Keep the highlighted option in view while arrow-keying */
  useEffect(() => {
    if (!open || !menuRef.current) return;

    const node = menuRef.current.children[activeIndex];

    if (node) node.scrollIntoView({ block: 'nearest' });
  }, [open, activeIndex]);

  const openMenu = () => {
    setActiveIndex(selectedIndex);
    setOpen(true);
  };

  const pick = (option) => {
    onChange(option.value);
    setOpen(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setOpen(false);

      return;
    }

    if (!open) {
      if (['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(e.key)) {
        e.preventDefault();
        openMenu();
      }

      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % options.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + options.length) % options.length);
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      pick(options[activeIndex]);
    } else if (e.key === 'Tab') {
      setOpen(false);
    }
  };

  return (
    <div className={`dsel ${open ? 'is-open' : ''}`} ref={boxRef}>
      <button
        type="button"
        className="dsel-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => (open ? setOpen(false) : openMenu())}
        onKeyDown={handleKeyDown}
      >
        <span className="dsel-trigger-text">{selected?.label}</span>
        <span className="dsel-caret" aria-hidden="true">
          ▾
        </span>
      </button>

      {open && (
        <ul className="dsel-menu" role="listbox" ref={menuRef}>
          {options.map((option, index) => (
            <li
              key={option.value || 'default'}
              role="option"
              aria-selected={option.value === value}
              className={`dsel-option${
                option.value === value ? ' is-selected' : ''
              }${index === activeIndex ? ' is-active' : ''}`}
              style={{ animationDelay: `${index * 45}ms` }}
              onMouseEnter={() => setActiveIndex(index)}
              onClick={() => pick(option)}
            >
              <span className="dsel-diya" aria-hidden="true" />
              <span className="dsel-option-text">{option.label}</span>
              <span className="dsel-tick" aria-hidden="true">
                ✦
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ProductsPage() {
  const [search, setSearch] = useState('');

  const [quickViewProduct, setQuickViewProduct] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('');

  const [sortBy, setSortBy] = useState('');

  const [maxPrice, setMaxPrice] = useState('');

  const [stockOnly, setStockOnly] = useState(false);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  /* Which wishlist buttons are mid-celebration. Keyed by product id, because
     the button is rendered once per card and only the one that was clicked
     should react. */
  const [burstIds, setBurstIds] = useState(new Set());
  const location = useLocation();

  /* Real customer ratings, shared across every listing on the page */
  const { getRating } = useProductRatings();

  const isAdmin = JSON.parse(
    localStorage.getItem('userInfo') || 'null'
  )?.isAdmin;

  const query = new URLSearchParams(location.search);

  const searchKeyword = query.get('search') || '';

  const addToWishlist = async (product) => {
    const userInfo = JSON.parse(localStorage.getItem('userInfo'));

    if (!userInfo) {
      toast.error('Please Login First');

      return;
    }

    try {
      await axios.post(`${API}/wishlist`, {
        userId: userInfo._id,

        productId: product._id,

        name: product.name,

        image: product.image,

        price: product.price
      });

      toast.success('Added To Wishlist ❤️');

      /* Only after the server accepted it — celebrating a failed add would
         tell the customer something happened that did not. */
      setBurstIds((prev) => new Set(prev).add(product._id));

      setTimeout(() => {
        setBurstIds((prev) => {
          const next = new Set(prev);
          next.delete(product._id);
          return next;
        });
      }, BURST_MS);
    } catch (error) {
      console.log(error);

      toast.error(error.response?.data?.message || 'Failed To Add Wishlist');
    }
  };
  useEffect(() => {
    if (searchKeyword) {
      setSearch(searchKeyword);
    }
  }, [searchKeyword]);
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const { data } = await axios.get(`${API}/products`);

        setProducts(data);
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  let filteredProducts = [...products];

  if (search.trim()) {
    filteredProducts = filteredProducts.filter(
      (product) =>
        product.name?.toLowerCase().includes(search.toLowerCase()) ||
        product.category?.toLowerCase().includes(search.toLowerCase())
    );
  }

  if (selectedCategory) {
    filteredProducts = filteredProducts.filter(
      (product) => product.category === selectedCategory
    );
  }

  if (maxPrice) {
    filteredProducts = filteredProducts.filter(
      (product) => Number(product.price) <= Number(maxPrice)
    );
  }

  if (stockOnly) {
    filteredProducts = filteredProducts.filter((product) => product.stock > 0);
  }

  if (sortBy === 'lowToHigh') {
    filteredProducts.sort((a, b) => a.price - b.price);
  }

  if (sortBy === 'highToLow') {
    filteredProducts.sort((a, b) => b.price - a.price);
  }

  if (sortBy === 'name') {
    filteredProducts.sort((a, b) => a.name.localeCompare(b.name));
  }
  const categories = [...new Set(products.map((product) => product.category))];

  const inStockCount = products.filter((product) => product.stock > 0).length;

  return (
    <>
      <ProductsBackdrop />

      <CustomNavbar />

      {/* Header */}

      <div className="products-header-wrap">
        <Container>
          <Card className="border-0 shadow-lg text-center products-hero">
            <span className="products-hero-aurora" />
            <span className="products-hero-blob products-hero-blob-1" />
            <span className="products-hero-blob products-hero-blob-2" />

            {/* Brass lamps hanging from the top corners */}
            <span className="ph-lamp ph-lamp-left" aria-hidden="true">
              <span className="ph-lamp-chain" />
              <span className="ph-lamp-body">
                <span className="ph-lamp-flame" />
                🪔
              </span>
            </span>

            <span className="ph-lamp ph-lamp-right" aria-hidden="true">
              <span className="ph-lamp-chain" />
              <span className="ph-lamp-body">
                <span className="ph-lamp-flame" />
                🪔
              </span>
            </span>

            {/* Motes drifting upward through the card */}
            <span className="ph-motes" aria-hidden="true">
              {Array.from({ length: 9 }).map((_, i) => (
                <span className="ph-mote" key={i} />
              ))}
            </span>

            {/* Incense wisps curling up from either side */}
            <span className="ph-smoke ph-smoke-1" aria-hidden="true" />
            <span className="ph-smoke ph-smoke-2" aria-hidden="true" />

            {/* Lotus opening and closing at the foot of the card */}
            <span className="ph-lotus" aria-hidden="true">
              {Array.from({ length: 9 }).map((_, i) => (
                <span
                  className="ph-petal"
                  key={i}
                  style={{
                    '--a': `${(i - 4) * 21}deg`,
                    animationDelay: `${(Math.abs(i - 4) * 0.09).toFixed(2)}s`
                  }}
                />
              ))}
              <span className="ph-lotus-core" />
            </span>

            <div className="position-relative">
              <span className="products-hero-tag">
                <span className="ph-om">ॐ</span> The Collection
              </span>

              <h1 className="fw-bold mb-3 products-hero-title">
                Explore{' '}
                <span className="ph-rotate">
                  <span className="ph-rotate-track">
                    <span className="ph-word">Products</span>
                    <span className="ph-word">Blessings</span>
                    <span className="ph-word">Idols</span>
                    <span className="ph-word">Offerings</span>
                    <span className="ph-word">Products</span>
                  </span>
                </span>
              </h1>

              <p className="mb-0 products-hero-text">
                Sacred idols, pooja essentials and blessings — chosen with care
                for your home temple.
              </p>

              {!loading && products.length > 0 && (
                <div className="ph-stats">
                  <div className="ph-stat">
                    <span className="ph-stat-num">{products.length}</span>
                    <span className="ph-stat-label">Products</span>
                  </div>

                  <span className="ph-stat-sep" />

                  <div className="ph-stat">
                    <span className="ph-stat-num">{categories.length}</span>
                    <span className="ph-stat-label">Categories</span>
                  </div>

                  <span className="ph-stat-sep" />

                  <div className="ph-stat">
                    <span className="ph-stat-num">{inStockCount}</span>
                    <span className="ph-stat-label">In Stock</span>
                  </div>
                </div>
              )}
            </div>
          </Card>
        </Container>
      </div>

      {/* Product Section */}

      <div className="products-body-wrap">
        <Container>
          <Row className="g-3 mb-4 align-items-stretch pp-toolbar">
            {/* Left: search + product count */}
            <Col lg={5}>
              <Card className="border-0 shadow-lg h-100 explorer-card">
                <span className="explorer-blob explorer-blob-1" />
                <span className="explorer-blob explorer-blob-2" />

                <Card.Body className="position-relative p-3 p-md-4 d-flex flex-column justify-content-center">
                  <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
                    <h4 className="fw-bold mb-0 d-flex align-items-center gap-2 explorer-title">
                      <span className="explorer-icon">🛍️</span> Explore
                    </h4>
                    <span className="explorer-count">
                      <span className="explorer-count-num">
                        {filteredProducts.length}
                      </span>
                      Found
                    </span>
                  </div>

                  <Form>
                    <InputGroup className="products-search">
                      <InputGroup.Text className="products-search-icon">
                        🔍
                      </InputGroup.Text>

                      <Form.Control
                        type="search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search products..."
                        className="products-search-input"
                      />

                      <Button
                        type="button"
                        className="products-search-btn fw-bold"
                      >
                        Search
                      </Button>
                    </InputGroup>
                  </Form>
                </Card.Body>
              </Card>
            </Col>

            {/* Right: filters */}
            <Col lg={7}>
              <Card className="border-0 shadow-lg h-100 filter-card">
                <div className="filter-card-header d-flex justify-content-between align-items-center">
                  {/* Floating temple embers drifting across the header */}
                  <span className="fch-embers" aria-hidden="true">
                    {[1, 2, 3, 4, 5, 6, 7].map((n) => (
                      <i key={n} className={`fch-ember fch-ember-${n}`} />
                    ))}
                  </span>

                  <h5 className="fw-bold mb-0 d-flex align-items-center gap-2 fch-title">
                    <span className="filter-icon-halo">
                      <span className="filter-icon">⚙️</span>
                    </span>
                    <span className="fch-title-text">Filter Products</span>
                  </h5>

                  <Button
                    variant="light"
                    size="sm"
                    className="rounded-pill px-3 fw-semibold filter-clear-btn"
                    onClick={() => {
                      setSelectedCategory('');
                      setMaxPrice('');
                      setSortBy('');
                      setStockOnly(false);
                    }}
                  >
                    ✕ Clear
                  </Button>
                </div>

                <Card.Body className="p-3">
                  <Row className="g-2 g-md-3">
                    <Col sm={6}>
                      <Form.Label className="filter-label">
                        📂 Category
                      </Form.Label>
                      <DivineSelect
                        ariaLabel="Filter by category"
                        value={selectedCategory}
                        onChange={setSelectedCategory}
                        options={[
                          { value: '', label: 'All Categories' },
                          ...categories.map((category) => ({
                            value: category,
                            label: category
                          }))
                        ]}
                      />
                    </Col>

                    <Col sm={6}>
                      <Form.Label className="filter-label">
                        💰 Max Price
                      </Form.Label>
                      <Form.Control
                        type="number"
                        className="filter-input"
                        placeholder="Any price"
                        value={maxPrice}
                        onChange={(e) => setMaxPrice(e.target.value)}
                      />
                    </Col>

                    <Col sm={6}>
                      <Form.Label className="filter-label">
                        🔄 Sort By
                      </Form.Label>
                      <DivineSelect
                        ariaLabel="Sort products"
                        value={sortBy}
                        onChange={setSortBy}
                        options={[
                          { value: '', label: 'Default' },
                          { value: 'lowToHigh', label: 'Price Low → High' },
                          { value: 'highToLow', label: 'Price High → Low' },
                          { value: 'name', label: 'Name A → Z' }
                        ]}
                      />
                    </Col>

                    <Col sm={6}>
                      <Form.Label className="filter-label">
                        📦 Availability
                      </Form.Label>
                      <div className="filter-switch-box">
                        <Form.Check
                          type="switch"
                          id="stock-switch"
                          label="Only In Stock"
                          checked={stockOnly}
                          onChange={(e) => setStockOnly(e.target.checked)}
                        />
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Category quick-filter chips */}
          {categories.length > 0 && (
            <div className="pp-chips">
              <button
                type="button"
                className={`pp-chip ${!selectedCategory ? 'is-active' : ''}`}
                onClick={() => setSelectedCategory('')}
              >
                ✨ All
              </button>
              {categories.map((category) => (
                <button
                  key={category}
                  type="button"
                  className={`pp-chip ${selectedCategory === category ? 'is-active' : ''}`}
                  onClick={() => setSelectedCategory(category)}
                >
                  {category}
                </button>
              ))}
            </div>
          )}

          {/* Products */}
          {searchKeyword && (
            <Card className="border-0 shadow-sm mb-4">
              <Card.Body>
                <h5 className="mb-0">
                  🔍 Search Results For
                  <span className="text-primary"> "{searchKeyword}"</span>
                </h5>
              </Card.Body>
            </Card>
          )}
          {(selectedCategory || maxPrice || sortBy || stockOnly) && (
            <Card className="border-0 shadow-sm mb-4">
              <Card.Body>
                <strong>Active Filters:</strong>{' '}
                {selectedCategory && (
                  <Badge bg="primary" className="me-2">
                    {selectedCategory}
                  </Badge>
                )}
                {maxPrice && (
                  <Badge bg="success" className="me-2">
                    Under ₹{maxPrice}
                  </Badge>
                )}
                {sortBy && (
                  <Badge bg="dark" className="me-2">
                    {sortBy}
                  </Badge>
                )}
                {stockOnly && (
                  <Badge bg="warning" text="dark">
                    In Stock
                  </Badge>
                )}
              </Card.Body>
            </Card>
          )}

          {loading ? (
            <Row className="g-4">
              {Array.from({ length: 6 }).map((_, n) => (
                <Col xl={6} lg={6} md={12} xs={12} key={n}>
                  <Card className="border-0 shadow-sm h-100 product-grid-card">
                    <div className="pp-skel pp-skel-img" />
                    <Card.Body>
                      <div className="pp-skel pp-skel-line short mb-2" />
                      <div className="pp-skel pp-skel-line mb-2" />
                      <div className="pp-skel pp-skel-line mb-3" style={{ width: '60%' }} />
                      <div className="pp-skel pp-skel-btn" />
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          ) : filteredProducts.length === 0 ? (
            <Card
              className="border-0 shadow-sm text-center py-5 my-4"
              style={{ borderRadius: '20px' }}
            >
              <Card.Body>
                <div style={{ fontSize: '3rem' }}>🔎</div>
                <h4 className="fw-bold mt-2">No products found</h4>
                <p className="text-muted mb-0">
                  Try adjusting your search or clearing the filters.
                </p>
              </Card.Body>
            </Card>
          ) : (
            <Row className="g-4">
              {filteredProducts.map((product, index) => {
                const inStock = product.stock > 0;

                return (
                  <Col
                    xl={6}
                    lg={6}
                    md={12}
                    xs={12}
                    key={product._id}
                    className="pp-card-in"
                    /* stagger caps out so late cards don't wait seconds */
                    style={{ animationDelay: `${Math.min(index, 11) * 70}ms` }}
                  >
                    <Card className="border-0 shadow-sm h-100 product-grid-card">
                      <span className="pp-card-bar" aria-hidden="true" />
                      {/* Product Image */}

                      <div className="product-grid-img-wrap">
                        {product.topSale && (
                          <Badge className="product-row-sale">🔥 TOP SALE</Badge>
                        )}
                        {!inStock && (
                          <Badge className="product-row-oos">SOLD OUT</Badge>
                        )}

                        <Card.Img
                          src={product.image}
                          alt={product.name}
                          className="product-grid-img"
                        />

                        <div className="product-grid-overlay">
                          <button
                            type="button"
                            className="product-grid-quick-btn"
                            onClick={() => setQuickViewProduct(product)}
                          >
                            <FaEye className="me-1" /> Quick View
                          </button>
                        </div>
                      </div>

                      {/* Product Details */}

                      <Card.Body className="d-flex flex-column">
                        <div className="mb-1 product-row-stars">
                          <StarRating
                            average={getRating(product._id).averageRating}
                            count={getRating(product._id).totalReviews}
                          />
                        </div>

                        <Card.Title className="fw-bold product-grid-name mb-1">
                          {product.name}
                        </Card.Title>

                        <p className="text-muted small product-grid-desc mb-2">
                          {product.shortDescription}
                        </p>

                        <div className="d-flex align-items-baseline gap-2 mb-2">
                          <span className="text-success fw-bold product-grid-price">
                            ₹{product.price}
                          </span>
                        </div>

                        <div className="mb-3">
                          {inStock ? (
                            <Badge
                              bg="success"
                              className="rounded-pill fw-normal pp-stock-badge"
                            >
                              <span className="pp-stock-dot" aria-hidden="true">
                                ●
                              </span>{' '}
                              In Stock
                            </Badge>
                          ) : (
                            <Badge
                              bg="secondary"
                              className="rounded-pill fw-normal"
                            >
                              Out of Stock
                            </Badge>
                          )}
                          <span className="text-muted small ms-2">
                            🚚 Free Delivery
                          </span>
                        </div>

                        <div className="mt-auto product-row-actions">
                          <Button
                            as={Link}
                            to={`/product/${product._id}`}
                            variant="dark"
                            className="fw-bold product-row-btn pp-btn"
                          >
                            <span className="pp-btn-label">View Details</span>
                            <span className="prb-arrow" aria-hidden="true">
                              →
                            </span>
                          </Button>

                          {isAdmin ? (
                            <Button
                              as={Link}
                              to={`/admin/edit-product/${product._id}`}
                              state={{
                                from: location.pathname + location.search
                              }}
                              variant="warning"
                              className="fw-semibold product-edit-btn pp-btn"
                            >
                              <span className="peb-pencil" aria-hidden="true">
                                ✏️
                              </span>
                              <span className="pp-btn-label">Edit Product</span>
                            </Button>
                          ) : (
                            <Button
                              variant="outline-danger"
                              className={`fw-semibold product-wishlist-btn pp-btn ${
                                burstIds.has(product._id) ? 'is-bursting' : ''
                              }`}
                              onClick={() => addToWishlist(product)}
                            >
                              {/* the ring that snaps outward on success */}
                              <span
                                className="pwb-ring"
                                aria-hidden="true"
                              />

                              <span className="pwb-heart" aria-hidden="true">
                                ❤️
                              </span>

                              <span className="pp-btn-label">
                                Add to Wishlist
                              </span>

                              {burstIds.has(product._id) && (
                                <span className="pwb-burst" aria-hidden="true">
                                  {BURST_HEARTS.map((h, i) => (
                                    <span
                                      key={i}
                                      className="pwb-burst-heart"
                                      style={{
                                        '--bx': `${
                                          Math.cos((h.a * Math.PI) / 180) * h.d
                                        }px`,
                                        '--by': `${
                                          Math.sin((h.a * Math.PI) / 180) * h.d
                                        }px`,
                                        '--bs': h.s,
                                        animationDelay: `${i * 0.035}s`
                                      }}
                                    >
                                      {h.char}
                                    </span>
                                  ))}
                                </span>
                              )}
                            </Button>
                          )}
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          )}
        </Container>
      </div>

      <QuickViewModal
        product={quickViewProduct}
        show={!!quickViewProduct}
        onHide={() => setQuickViewProduct(null)}
      />

      <style>{productsPageStyles}</style>
    </>
  );
}

const productsPageStyles = `
  /* Both wraps are transparent — the animated .pp-bg layer paints the page */
  .products-header-wrap {
    background: transparent;
    padding: clamp(36px, 6vw, 60px) 0;
    position: relative;
  }

  /* ===== Animated page backdrop ===== */
  .pp-bg {
    position: fixed;
    inset: 0;
    z-index: -1;
    overflow: hidden;
    pointer-events: none;
    background:
      radial-gradient(760px 340px at 8% -8%, rgba(124,58,237,0.14), transparent 70%),
      radial-gradient(760px 340px at 92% 108%, rgba(234,88,12,0.16), transparent 70%),
      linear-gradient(180deg, #faf5ff 0%, #fff7ed 55%, #fef3c7 100%);
  }

  /* Second palette crossfading over the base — opacity only, so it composites
     on the GPU instead of repainting the gradient every frame */
  .pp-bg-veil {
    position: absolute;
    inset: -20%;
    background:
      radial-gradient(50% 55% at 20% 25%, rgba(251,191,36,0.30), transparent 65%),
      radial-gradient(46% 50% at 82% 30%, rgba(168,85,247,0.24), transparent 65%),
      radial-gradient(55% 60% at 55% 88%, rgba(234,88,12,0.26), transparent 65%);
    opacity: 0.45;
    animation: ppVeil 26s ease-in-out infinite alternate;
    will-change: transform, opacity;
  }
  @keyframes ppVeil {
    0%   { opacity: 0.30; transform: translate3d(-2%, 1%, 0) scale(1); }
    100% { opacity: 0.70; transform: translate3d(3%, -2%, 0) scale(1.14); }
  }

  /* Slow-drifting blurred aurora — the backdrop's main ambient motion */
  .pp-aurora {
    position: absolute;
    inset: -35%;
    background:
      radial-gradient(34% 40% at 26% 30%, rgba(249,115,22,0.34), transparent 62%),
      radial-gradient(30% 36% at 74% 24%, rgba(139,92,246,0.30), transparent 62%),
      radial-gradient(38% 44% at 60% 80%, rgba(253,230,138,0.40), transparent 62%),
      radial-gradient(28% 34% at 12% 76%, rgba(244,114,182,0.24), transparent 62%);
    filter: blur(50px);
    animation: ppAurora 30s ease-in-out infinite alternate;
    will-change: transform;
  }
  @keyframes ppAurora {
    0%   { transform: translate3d(-4%, -3%, 0) rotate(0deg)  scale(1.05); }
    50%  { transform: translate3d(5%, 4%, 0)   rotate(9deg)  scale(1.2); }
    100% { transform: translate3d(-3%, 6%, 0)  rotate(-7deg) scale(1.1); }
  }

  /* Mandala rings turning in opposite directions */
  .pp-mandala {
    position: absolute;
    border-radius: 50%;
    background: repeating-conic-gradient(from 0deg,
      rgba(180,83,9,0.5) 0deg 2.5deg, transparent 2.5deg 11deg);
    -webkit-mask-image: radial-gradient(circle, transparent 40%, #000 44%, #000 68%, transparent 73%);
    mask-image: radial-gradient(circle, transparent 40%, #000 44%, #000 68%, transparent 73%);
    will-change: transform;
  }
  .pp-mandala-1 {
    width: 620px; height: 620px;
    top: -180px; left: -170px;
    opacity: 0.14;
    animation: ppSpin 110s linear infinite;
  }
  .pp-mandala-2 {
    width: 480px; height: 480px;
    bottom: -150px; right: -130px;
    opacity: 0.11;
    background: repeating-conic-gradient(from 0deg,
      rgba(124,58,237,0.5) 0deg 2deg, transparent 2deg 9deg);
    animation: ppSpin 78s linear infinite reverse;
  }
  @keyframes ppSpin {
    to { transform: rotate(360deg); }
  }

  /* Shafts of light sweeping left to right */
  .pp-ray {
    position: absolute;
    top: -30%;
    bottom: -30%;
    left: 0;
    width: 16%;
    background: linear-gradient(100deg,
      transparent, rgba(255,255,255,0.35) 40%, rgba(253,230,138,0.45) 52%,
      rgba(255,255,255,0.28) 64%, transparent);
    transform: skewX(-14deg) translate3d(-40vw, 0, 0);
    filter: blur(8px);
    animation: ppRay 22s ease-in-out infinite;
    will-change: transform;
  }
  .pp-ray-2 { width: 9%;  opacity: 0.7; animation-duration: 29s; animation-delay: -9s; }
  .pp-ray-3 { width: 22%; opacity: 0.5; animation-duration: 36s; animation-delay: -19s; }
  @keyframes ppRay {
    0%, 45% { transform: skewX(-14deg) translate3d(-40vw, 0, 0); }
    100%    { transform: skewX(-14deg) translate3d(130vw, 0, 0); }
  }

  /* Twinkling embers scattered across the viewport */
  .pp-spark {
    position: absolute;
    border-radius: 50%;
    background: radial-gradient(circle at 40% 35%, #fffbeb, #fbbf24 60%, rgba(245,158,11,0));
    box-shadow: 0 0 10px rgba(251,191,36,0.8);
    opacity: 0;
    animation-name: ppTwinkle;
    animation-timing-function: ease-in-out;
    animation-iteration-count: infinite;
  }
  @keyframes ppTwinkle {
    0%, 100% { opacity: 0;    transform: scale(0.4); }
    50%      { opacity: 0.85; transform: scale(1.35); }
  }

  /* Offerings drifting upward off the bottom edge */
  .pp-float {
    position: absolute;
    bottom: -70px;
    line-height: 1;
    opacity: 0;
    color: #b45309;
    filter: drop-shadow(0 6px 14px rgba(180,83,9,0.22));
    animation-name: ppRise;
    animation-timing-function: linear;
    animation-iteration-count: infinite;
    will-change: transform, opacity;
  }
  @keyframes ppRise {
    0%   { opacity: 0;    transform: translate3d(0, 0, 0) rotate(0deg) scale(0.85); }
    10%  { opacity: 0.55; }
    50%  { transform: translate3d(34px, -55vh, 0) rotate(14deg) scale(1); }
    88%  { opacity: 0.4; }
    100% { opacity: 0;    transform: translate3d(-26px, -115vh, 0) rotate(-12deg) scale(1.12); }
  }

  @media (max-width: 767.98px) {
    /* Fewer moving parts on phones */
    .pp-mandala-2, .pp-ray-3, .pp-float-extra { display: none; }
  }

  @media (prefers-reduced-motion: reduce) {
    .pp-bg-veil,
    .pp-aurora,
    .pp-mandala,
    .pp-ray,
    .pp-spark,
    .pp-float { animation: none !important; }
    .pp-ray, .pp-float { display: none; }
    .pp-spark { opacity: 0.5; }
  }
  /* ===== Explore Products hero ===== */
  /* Sanctum palette: lamp-black → temple maroon → saffron → brass */
  .products-hero {
    border-radius: 30px;
    padding: clamp(36px, 6vw, 68px);
    color: #fff7ed;
    background: linear-gradient(120deg, #1a0611 0%, #460d16 32%, #7c2d12 62%, #b45309 84%, #d97706 100%);
    border: 1px solid rgba(253, 230, 138, 0.28) !important;
    overflow: hidden;
    position: relative;
    isolation: isolate;
  }
  /* Slow-drifting aurora wash — the hero's ambient motion */
  .products-hero-aurora {
    position: absolute;
    inset: -40%;
    z-index: 0;
    pointer-events: none;
    background:
      radial-gradient(38% 46% at 22% 28%, rgba(251, 191, 36, 0.45), transparent 62%),
      radial-gradient(34% 42% at 76% 26%, rgba(168, 85, 247, 0.40), transparent 62%),
      radial-gradient(42% 48% at 58% 82%, rgba(234, 88, 12, 0.45), transparent 62%),
      radial-gradient(30% 40% at 14% 78%, rgba(253, 230, 138, 0.35), transparent 62%);
    filter: blur(26px);
    animation: heroAurora 22s ease-in-out infinite alternate;
  }
  .products-hero > .position-relative { z-index: 3; }

  /* Brass lamps hanging from the top edge */
  .ph-lamp {
    position: absolute;
    top: 0;
    z-index: 2;
    display: flex;
    flex-direction: column;
    align-items: center;
    pointer-events: none;
    transform-origin: 50% 0;
    animation: phLampSway 4.6s ease-in-out infinite;
  }
  .ph-lamp-left  { left: 7%; }
  .ph-lamp-right { right: 7%; animation-delay: -2.3s; }
  .ph-lamp-chain {
    width: 2px;
    height: clamp(26px, 5vw, 48px);
    background: linear-gradient(180deg, rgba(253,230,138,0.85), rgba(253,230,138,0.35));
  }
  .ph-lamp-body {
    position: relative;
    font-size: 1.5rem;
    line-height: 1;
    filter: drop-shadow(0 4px 10px rgba(0, 0, 0, 0.4));
  }
  /* A live flame sitting in the lamp's mouth */
  .ph-lamp-flame {
    position: absolute;
    left: 50%;
    top: -11px;
    width: 8px; height: 15px;
    margin-left: -4px;
    border-radius: 50% 50% 50% 50% / 62% 62% 38% 38%;
    background: radial-gradient(circle at 50% 72%, #fffbeb 0%, #fde68a 28%, #fbbf24 55%, rgba(249,115,22,0.9) 78%, rgba(249,115,22,0) 100%);
    box-shadow: 0 0 20px rgba(251, 191, 36, 0.9);
    transform-origin: 50% 100%;
    animation: phFlame 1.05s ease-in-out infinite;
  }
  .ph-lamp-right .ph-lamp-flame { animation-duration: 0.88s; animation-delay: -0.4s; }
  /* Uneven steps so the flame never reads as a loop */
  @keyframes phFlame {
    0%, 100% { transform: scale(1, 1)      rotate(-2deg); opacity: 0.92; }
    22%      { transform: scale(0.86, 1.22) rotate(4deg); opacity: 1; }
    47%      { transform: scale(1.12, 0.9)  rotate(-5deg); opacity: 0.8; }
    71%      { transform: scale(0.94, 1.1)  rotate(3deg); opacity: 1; }
  }
  @keyframes phLampSway {
    0%, 100% { transform: rotate(-3.5deg); }
    50%      { transform: rotate(3.5deg); }
  }

  .products-hero-tag {
    display: inline-block;
    font-size: 0.68rem;
    font-weight: 800;
    letter-spacing: 4px;
    text-transform: uppercase;
    color: #fde68a;
    padding-bottom: 10px;
    margin-bottom: 6px;
    border-bottom: 1px solid rgba(253, 230, 138, 0.35);
  }
  /* ॐ pulses in a three-beat chant, then rests */
  .ph-om {
    display: inline-block;
    margin-right: 6px;
    font-size: 1.15em;
    color: #fbbf24;
    text-shadow: 0 0 14px rgba(251, 191, 36, 0.7);
    animation: phChant 4.5s ease-in-out infinite;
  }
  @keyframes phChant {
    0%           { transform: scale(1);    opacity: 0.7; }
    7%           { transform: scale(1.3);  opacity: 1; }
    15%          { transform: scale(1);    opacity: 0.75; }
    22%          { transform: scale(1.24); opacity: 1; }
    30%          { transform: scale(1);    opacity: 0.75; }
    37%          { transform: scale(1.16); opacity: 0.95; }
    45%, 100%    { transform: scale(1);    opacity: 0.7; }
  }

  /* Incense smoke curling upward */
  .ph-smoke {
    position: absolute;
    bottom: 0;
    width: 4px;
    height: 130px;
    z-index: 1;
    pointer-events: none;
    border-radius: 4px;
    background: linear-gradient(180deg, rgba(255,247,237,0) 0%, rgba(255,247,237,0.4) 45%, rgba(255,247,237,0.12) 100%);
    filter: blur(7px);
    opacity: 0;
    animation: phSmoke 9s ease-in-out infinite;
  }
  .ph-smoke-1 { left: 11%; }
  .ph-smoke-2 { right: 13%; height: 155px; animation-delay: -4.5s; }
  @keyframes phSmoke {
    0%   { opacity: 0;    transform: translate(0, 24px)    scaleY(0.5) skewX(0deg); }
    18%  { opacity: 0.75; }
    50%  { transform: translate(16px, -70px)  scaleY(1)   skewX(-9deg); }
    82%  { opacity: 0.3; }
    100% { opacity: 0;    transform: translate(-12px, -170px) scaleY(1.35) skewX(9deg); }
  }

  /* Lotus opening and closing at the foot of the card */
  .ph-lotus {
    position: absolute;
    left: 50%;
    bottom: -14px;
    width: 0; height: 0;
    z-index: 1;
    pointer-events: none;
  }
  .ph-petal {
    position: absolute;
    left: -10px;
    bottom: 0;
    width: 20px;
    height: 58px;
    border-radius: 50% 50% 50% 50% / 62% 62% 38% 38%;
    background: linear-gradient(180deg, rgba(253,230,138,0.8), rgba(244,114,182,0.35) 58%, rgba(244,114,182,0));
    transform-origin: 50% 100%;
    animation: phBloom 7.5s ease-in-out infinite;
  }
  /* Petals fan out as they grow, then fold back in */
  @keyframes phBloom {
    0%, 100% { transform: rotate(calc(var(--a) * 0.4)) scaleY(0.5);  opacity: 0.3; }
    50%      { transform: rotate(var(--a))             scaleY(1);    opacity: 0.8; }
  }
  .ph-lotus-core {
    position: absolute;
    left: -7px; bottom: -4px;
    width: 14px; height: 14px;
    border-radius: 50%;
    background: radial-gradient(circle at 40% 35%, #fffbeb, #fbbf24 65%, rgba(180,83,9,0));
    box-shadow: 0 0 18px rgba(251, 191, 36, 0.75);
    animation: phCore 7.5s ease-in-out infinite;
  }
  @keyframes phCore {
    0%, 100% { transform: scale(0.7); opacity: 0.45; }
    50%      { transform: scale(1.1); opacity: 1; }
  }
  .products-hero-title {
    font-size: clamp(2rem, 6vw, 3rem);
    letter-spacing: -0.5px;
  }
  /* Vertical word rotator — the second half of the headline cycles */
  .ph-rotate {
    display: inline-block;
    height: 1.18em;
    line-height: 1.18em;
    overflow: hidden;
    vertical-align: bottom;
  }
  .ph-rotate-track {
    display: flex;
    flex-direction: column;
    animation: heroWords 11s cubic-bezier(0.85, 0, 0.15, 1) infinite;
  }
  .ph-word {
    height: 1.18em;
    line-height: 1.18em;
    background: linear-gradient(100deg, #fde68a, #fbbf24 45%, #f0abfc 100%);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
  }
  .products-hero-text {
    font-size: clamp(0.95rem, 2.5vw, 1.15rem);
    opacity: 0.85;
    max-width: 640px;
    margin: 0 auto;
  }

  /* Live catalogue stats */
  .ph-stats {
    display: inline-flex;
    align-items: center;
    gap: clamp(14px, 4vw, 34px);
    margin-top: 28px;
    padding: 14px clamp(18px, 4vw, 34px);
    border-radius: 22px;
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.16);
    backdrop-filter: blur(10px);
  }
  .ph-stat { line-height: 1.1; }
  .ph-stat-num {
    display: block;
    font-size: clamp(1.3rem, 3.5vw, 1.8rem);
    font-weight: 800;
    background: linear-gradient(135deg, #fff, #fde68a);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
  }
  .ph-stat-label {
    display: block;
    font-size: 0.66rem;
    font-weight: 600;
    letter-spacing: 1.2px;
    text-transform: uppercase;
    opacity: 0.7;
    margin-top: 3px;
  }
  .ph-stat-sep {
    width: 1px;
    height: 30px;
    background: linear-gradient(180deg, transparent, rgba(255,255,255,0.4), transparent);
  }

  /* Motes drifting up through the hero */
  .ph-motes {
    position: absolute;
    inset: 0;
    z-index: 2;
    pointer-events: none;
    overflow: hidden;
  }
  .ph-mote {
    position: absolute;
    bottom: -14px;
    width: 7px; height: 7px;
    border-radius: 50%;
    background: rgba(253, 230, 138, 0.85);
    box-shadow: 0 0 12px rgba(251, 191, 36, 0.9);
    opacity: 0;
    animation: heroMote 13s linear infinite;
  }
  .ph-mote:nth-child(1) { left: 8%;  animation-delay: 0s;   }
  .ph-mote:nth-child(2) { left: 19%; animation-delay: 4.5s; width: 5px; height: 5px; }
  .ph-mote:nth-child(3) { left: 31%; animation-delay: 1.8s; }
  .ph-mote:nth-child(4) { left: 44%; animation-delay: 7.2s; width: 4px; height: 4px; }
  .ph-mote:nth-child(5) { left: 56%; animation-delay: 2.9s; }
  .ph-mote:nth-child(6) { left: 67%; animation-delay: 9.4s; width: 5px; height: 5px; }
  .ph-mote:nth-child(7) { left: 78%; animation-delay: 5.6s; }
  .ph-mote:nth-child(8) { left: 88%; animation-delay: 3.4s; width: 4px; height: 4px; }
  .ph-mote:nth-child(9) { left: 95%; animation-delay: 8.1s; }

  .products-hero-blob {
    position: absolute;
    border-radius: 50%;
    filter: blur(60px);
    opacity: 0.4;
    pointer-events: none;
    z-index: 0;
  }
  .products-hero-blob-1 {
    width: 240px; height: 240px;
    background: #ea580c;
    top: -90px; left: -60px;
  }
  .products-hero-blob-2 {
    width: 220px; height: 220px;
    background: #a855f7;
    bottom: -90px; right: -40px;
    opacity: 0.35;
  }

  @keyframes heroAurora {
    0%   { transform: translate3d(-4%, -3%, 0) rotate(0deg) scale(1.05); }
    50%  { transform: translate3d(5%, 4%, 0) rotate(8deg) scale(1.18); }
    100% { transform: translate3d(-3%, 5%, 0) rotate(-6deg) scale(1.08); }
  }
  @keyframes heroWords {
    0%, 18%   { transform: translateY(0); }
    25%, 43%  { transform: translateY(-1.18em); }
    50%, 68%  { transform: translateY(-2.36em); }
    75%, 93%  { transform: translateY(-3.54em); }
    100%      { transform: translateY(-4.72em); }
  }
  @keyframes heroMote {
    0%   { opacity: 0; transform: translateY(0) translateX(0) scale(0.6); }
    12%  { opacity: 1; }
    80%  { opacity: 0.7; }
    100% { opacity: 0; transform: translateY(-460px) translateX(26px) scale(1.1); }
  }

  @media (max-width: 575.98px) {
    .ph-stats { gap: 14px; padding: 12px 16px; }
    .products-hero-tag { letter-spacing: 2.5px; }
  }

  .products-body-wrap {
    background: transparent;
    position: relative;
    min-height: 100vh;
    padding-bottom: 50px;
    padding-top: 10px;
  }

  /* Search bar */
  .products-search {
    border-radius: 50px;
    overflow: hidden;
    background: #fff;
    box-shadow: 0 12px 34px rgba(194, 65, 12, 0.14);
    border: 2px solid rgba(245, 158, 11, 0.28);
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
  }
  .products-search:focus-within {
    border-color: #f59e0b;
    box-shadow: 0 14px 42px rgba(245, 158, 11, 0.32);
  }
  .products-search-icon {
    background: #fff;
    border: none;
    font-size: 1.05rem;
    padding-left: 18px;
  }
  .products-search-input {
    border: none;
    padding: 10px 12px;
    font-size: 0.95rem;
    box-shadow: none !important;
  }
  .products-search-input:focus {
    background: #fff;
  }
  .products-search-btn {
    border: none;
    border-radius: 50px !important;
    margin: 4px;
    padding: 7px 24px;
    background: linear-gradient(135deg, #ea580c, #f59e0b);
    color: #fff;
    font-weight: 700;
    box-shadow: 0 6px 16px rgba(234, 88, 12, 0.35);
    transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
  }
  .products-search-btn:hover {
    background: linear-gradient(135deg, #fbbf24, #f97316);
    transform: translateY(-2px);
    box-shadow: 0 12px 24px rgba(245, 158, 11, 0.5);
  }

  /* Product Explorer banner */
  .explorer-card {
    border-radius: 22px;
    overflow: hidden;
    color: #fff;
    background:
      radial-gradient(700px 160px at 88% -40%, rgba(251,191,36,0.30), transparent 70%),
      linear-gradient(120deg, #3b0764 0%, #7c2d12 50%, #ea580c 100%);
    background-size: 160% 100%;
    animation: explorerSheen 14s ease infinite;
    position: relative;
    box-shadow: 0 16px 42px rgba(234, 88, 12, 0.25);
  }
  /* Shimmering gold thread along the top */
  .explorer-card::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 2px;
    background: linear-gradient(90deg,
      transparent, #fde68a 20%, #f59e0b 50%, #fde68a 80%, transparent);
    background-size: 200% 100%;
    animation: explorerThread 6s linear infinite;
    z-index: 2;
  }
  @keyframes explorerSheen {
    0%, 100% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
  }
  @keyframes explorerThread {
    from { background-position: 200% 0; }
    to { background-position: -200% 0; }
  }
  .explorer-blob {
    position: absolute;
    border-radius: 50%;
    filter: blur(55px);
    opacity: 0.5;
    pointer-events: none;
  }
  .explorer-blob-1 {
    width: 200px; height: 200px;
    background: #f59e0b;
    top: -80px; right: 20%;
  }
  .explorer-blob-2 {
    width: 180px; height: 180px;
    background: #a855f7;
    bottom: -90px; left: -40px;
    opacity: 0.4;
  }
  .explorer-title { font-size: 1.3rem; }
  .explorer-icon {
    display: inline-block;
    animation: explorerBob 2.5s ease-in-out infinite;
  }
  .explorer-subtitle {
    opacity: 0.85;
    font-size: 0.86rem;
  }
  .explorer-count {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: rgba(255,255,255,0.14);
    backdrop-filter: blur(8px);
    border: 1px solid rgba(253,230,138,0.4);
    border-radius: 50px;
    padding: 6px 18px 6px 8px;
    font-weight: 600;
    font-size: 0.9rem;
  }
  .explorer-count-num {
    background: linear-gradient(135deg, #fde68a, #fbbf24);
    color: #3b0764;
    font-weight: 800;
    border-radius: 50px;
    padding: 3px 12px;
    font-size: 1rem;
    box-shadow: 0 0 0 3px rgba(251,191,36,0.18);
    animation: explorerPulse 2.4s ease-in-out infinite;
  }
  @keyframes explorerBob {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-5px); }
  }
  @keyframes explorerPulse {
    0%, 100% { box-shadow: 0 0 0 3px rgba(251,191,36,0.15); }
    50% { box-shadow: 0 0 0 6px rgba(251,191,36,0.28); }
  }

  /* Filter card */
  .filter-card {
    border-radius: 22px;
    /* visible so the divine dropdown can overflow the card */
    overflow: visible;
    position: relative;
    z-index: 20;
    border: 1px solid rgba(245, 158, 11, 0.18);
    box-shadow: 0 14px 40px rgba(124, 45, 18, 0.12);
  }
  .filter-card-header {
    position: relative;
    overflow: hidden;
    isolation: isolate;
    border-radius: 21px 21px 0 0;
    /* Slowly drifting aurora over the temple gradient */
    background:
      radial-gradient(600px 130px at 12% -60%, rgba(251,191,36,0.32), transparent 70%),
      linear-gradient(120deg, #3b0764 0%, #7c2d12 35%, #b45309 55%, #7c2d12 78%, #3b0764 100%);
    background-size: 180% 180%, 260% 100%;
    animation: fchAurora 14s ease-in-out infinite;
    color: #fff;
    padding: 11px 18px;
  }
  .filter-card-header h5 { font-size: 1.05rem; }
  .filter-card-header > h5,
  .filter-card-header > .filter-clear-btn {
    position: relative;
    z-index: 2;
  }
  /* Divine light ray sweeping across the header */
  .filter-card-header::before {
    content: '';
    position: absolute;
    top: -60%; bottom: -60%;
    left: -40%;
    width: 26%;
    z-index: 1;
    background: linear-gradient(100deg,
      transparent, rgba(255,255,255,0.10) 35%, rgba(253,230,138,0.42) 50%,
      rgba(255,255,255,0.10) 65%, transparent);
    transform: skewX(-16deg);
    filter: blur(1px);
    animation: fchRay 7s ease-in-out infinite;
    pointer-events: none;
  }
  /* Gold thread under the header — now shimmering */
  .filter-card-header::after {
    content: '';
    position: absolute;
    left: 0; right: 0; bottom: 0;
    z-index: 3;
    height: 2px;
    background: linear-gradient(90deg,
      transparent, #fde68a 20%, #f59e0b 50%, #fde68a 80%, transparent);
    background-size: 220% 100%;
    animation: fchThread 3.2s linear infinite;
  }

  /* Floating embers */
  .fch-embers {
    position: absolute;
    inset: 0;
    z-index: 1;
    pointer-events: none;
  }
  .fch-ember {
    position: absolute;
    bottom: -6px;
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: radial-gradient(circle at 35% 35%, #fff7ea, #fbbf24 55%, rgba(245,158,11,0));
    opacity: 0;
    animation: fchEmberRise 6.5s ease-in-out infinite;
  }
  /* Size varies per ember (transform is owned by the rise animation) */
  .fch-ember-1 { left: 8%;  animation-delay: 0s;   animation-duration: 6.2s; }
  .fch-ember-2 { left: 21%; animation-delay: 1.1s; animation-duration: 7.4s; width: 3px; height: 3px; }
  .fch-ember-3 { left: 35%; animation-delay: 2.4s; animation-duration: 5.8s; width: 6px; height: 6px; }
  .fch-ember-4 { left: 52%; animation-delay: 0.6s; animation-duration: 8s;   width: 3px; height: 3px; }
  .fch-ember-5 { left: 68%; animation-delay: 3.2s; animation-duration: 6.6s; }
  .fch-ember-6 { left: 81%; animation-delay: 1.8s; animation-duration: 7.8s; width: 4px; height: 4px; }
  .fch-ember-7 { left: 93%; animation-delay: 4.1s; animation-duration: 6s;   width: 3px; height: 3px; }

  /* Title with a gold light travelling through the letters */
  .fch-title-text {
    background: linear-gradient(100deg,
      #ffffff 0%, #ffffff 32%, #fde68a 46%, #fbbf24 52%, #fde68a 58%, #ffffff 74%, #ffffff 100%);
    background-size: 250% 100%;
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    color: transparent;
    animation: fchTitleShine 5s ease-in-out infinite;
  }

  /* Pulsing halo behind the spinning gear */
  .filter-icon-halo {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
  }
  .filter-icon-halo::before {
    content: '';
    position: absolute;
    inset: -2px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(251,191,36,0.55), rgba(251,191,36,0) 68%);
    animation: fchHalo 2.6s ease-in-out infinite;
  }
  .filter-icon {
    position: relative;
    display: inline-block;
    animation: filterSpin 6s linear infinite;
    filter: drop-shadow(0 0 4px rgba(253, 230, 138, 0.75));
  }
  .filter-clear-btn {
    position: relative;
    overflow: hidden;
    color: #b45309;
    border: 1px solid rgba(253,230,138,0.6);
    box-shadow: 0 0 0 0 rgba(253, 230, 138, 0.5);
    transition: background 0.2s ease, color 0.2s ease, transform 0.2s ease,
      box-shadow 0.3s ease;
    animation: fchClearGlow 3.4s ease-in-out infinite;
  }
  .filter-clear-btn::after {
    content: '';
    position: absolute;
    top: 0; bottom: 0;
    left: -70%;
    width: 45%;
    background: linear-gradient(100deg, transparent, rgba(255,255,255,0.85), transparent);
    transform: skewX(-20deg);
    transition: left 0.55s ease;
  }
  .filter-clear-btn:hover {
    background: linear-gradient(135deg, #fde68a, #fbbf24);
    color: #3b0764;
    transform: translateY(-1px) scale(1.04);
  }
  .filter-clear-btn:hover::after { left: 130%; }

  @keyframes fchAurora {
    0%, 100% { background-position: 0% 50%, 0% 50%; }
    50%      { background-position: 100% 50%, 100% 50%; }
  }
  @keyframes fchRay {
    0%, 55%  { left: -40%; }
    100%     { left: 130%; }
  }
  @keyframes fchThread {
    to { background-position: 220% 0; }
  }
  @keyframes fchEmberRise {
    0%   { opacity: 0; transform: translate(0, 0) scale(0.5); }
    12%  { opacity: 0.9; }
    70%  { opacity: 0.75; }
    100% { opacity: 0; transform: translate(14px, -52px) scale(1.05); }
  }
  @keyframes fchTitleShine {
    0%, 100% { background-position: 130% 0; }
    50%      { background-position: -30% 0; }
  }
  @keyframes fchHalo {
    0%, 100% { opacity: 0.45; transform: scale(0.85); }
    50%      { opacity: 0.95; transform: scale(1.15); }
  }
  @keyframes fchClearGlow {
    0%, 100% { box-shadow: 0 0 0 0 rgba(253, 230, 138, 0.45); }
    50%      { box-shadow: 0 0 0 5px rgba(253, 230, 138, 0); }
  }
  @media (prefers-reduced-motion: reduce) {
    .filter-card-header,
    .filter-card-header::before,
    .filter-card-header::after,
    .fch-ember,
    .fch-title-text,
    .filter-icon,
    .filter-icon-halo::before,
    .filter-clear-btn { animation: none; }
    .fch-ember { opacity: 0; }
    .fch-title-text {
      -webkit-text-fill-color: #fff;
      color: #fff;
    }
  }
  .filter-label {
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: #9a3412;
    margin-bottom: 3px;
  }
  .filter-input {
    border-radius: 10px;
    padding: 7px 12px;
    font-size: 0.9rem;
    border: 1px solid #fde68a;
    background: #fffdf7;
    transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
  }
  .filter-input:focus {
    background: #fff;
    border-color: #f59e0b;
    box-shadow: 0 0 0 0.2rem rgba(245, 158, 11, 0.18);
  }
  .filter-switch-box {
    display: flex;
    align-items: center;
    height: 38px;
    padding: 0 12px;
    border-radius: 10px;
    background: #fffdf7;
    border: 1px solid #fde68a;
    font-size: 0.9rem;
  }
  .filter-switch-box .form-check-input:checked {
    background-color: #ea580c;
    border-color: #ea580c;
  }
  @keyframes filterSpin {
    to { transform: rotate(360deg); }
  }

  /* ===== Divine dropdown ===== */
  .dsel {
    position: relative;
  }
  .dsel.is-open { z-index: 60; }

  .dsel-trigger {
    position: relative;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    text-align: left;
    border-radius: 10px;
    padding: 7px 12px;
    font-family: inherit;
    font-size: 0.9rem;
    color: #431407;
    border: 1px solid #fde68a;
    background: #fffdf7;
    overflow: hidden;
    transition: border-color 0.25s ease, box-shadow 0.25s ease,
      background 0.25s ease, transform 0.25s ease;
  }
  /* Slow gold light sweeping across the closed control */
  .dsel-trigger::before {
    content: '';
    position: absolute;
    top: 0; bottom: 0;
    left: -60%;
    width: 45%;
    background: linear-gradient(100deg,
      transparent, rgba(253, 230, 138, 0.55), transparent);
    transform: skewX(-18deg);
    animation: dselSheen 5.5s ease-in-out infinite;
    pointer-events: none;
  }
  .dsel-trigger:hover {
    border-color: #f59e0b;
    background: #fff;
    transform: translateY(-1px);
  }
  .dsel-trigger:focus-visible {
    outline: none;
    border-color: #f59e0b;
    box-shadow: 0 0 0 0.2rem rgba(245, 158, 11, 0.18);
  }
  .dsel.is-open .dsel-trigger {
    background: #fff;
    border-color: #f59e0b;
    box-shadow: 0 0 0 0.2rem rgba(245, 158, 11, 0.22),
      0 8px 22px rgba(180, 83, 9, 0.18);
  }
  .dsel-trigger-text {
    position: relative;
    z-index: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .dsel-caret {
    position: relative;
    z-index: 1;
    color: #b45309;
    font-size: 0.85rem;
    line-height: 1;
    transition: transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1), color 0.25s ease;
  }
  .dsel.is-open .dsel-caret {
    transform: rotate(180deg) scale(1.15);
    color: #ea580c;
  }

  .dsel-menu {
    position: absolute;
    top: calc(100% + 8px);
    left: 0;
    right: 0;
    z-index: 60;
    margin: 0;
    padding: 6px;
    list-style: none;
    max-height: 268px;
    overflow-y: auto;
    border-radius: 16px;
    border: 1px solid rgba(245, 158, 11, 0.35);
    background:
      radial-gradient(420px 120px at 15% -20%, rgba(251, 191, 36, 0.22), transparent 70%),
      linear-gradient(180deg, #fffdf7 0%, #fff7ea 100%);
    box-shadow: 0 22px 46px rgba(124, 45, 18, 0.22),
      0 0 0 1px rgba(255, 255, 255, 0.6) inset;
    transform-origin: top center;
    animation: dselMenuIn 0.32s cubic-bezier(0.22, 1.4, 0.4, 1) both;
  }
  /* Shimmering gold thread along the top edge of the menu */
  .dsel-menu::before {
    content: '';
    position: absolute;
    left: 12px; right: 12px; top: 0;
    height: 2px;
    border-radius: 2px;
    background: linear-gradient(90deg,
      transparent, #fde68a 20%, #f59e0b 50%, #fde68a 80%, transparent);
    background-size: 200% 100%;
    animation: dselThread 2.6s linear infinite;
  }

  .dsel-option {
    position: relative;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 9px 12px;
    border-radius: 11px;
    font-size: 0.9rem;
    color: #431407;
    cursor: pointer;
    overflow: hidden;
    animation: dselOptionIn 0.34s ease both;
    transition: color 0.2s ease, transform 0.2s ease;
  }
  /* Divine wash that unrolls under the highlighted option */
  .dsel-option::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 11px;
    background: linear-gradient(120deg, #3b0764 0%, #7c2d12 55%, #b45309 100%);
    transform: scaleX(0);
    transform-origin: left center;
    transition: transform 0.28s cubic-bezier(0.22, 1, 0.36, 1);
  }
  .dsel-option.is-active {
    color: #fff7ea;
    transform: translateX(3px);
  }
  .dsel-option.is-active::before { transform: scaleX(1); }

  .dsel-diya {
    position: relative;
    z-index: 1;
    flex: none;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: linear-gradient(135deg, #fde68a, #f59e0b);
    box-shadow: 0 0 0 0 rgba(251, 191, 36, 0.55);
    transition: box-shadow 0.3s ease, transform 0.3s ease;
  }
  .dsel-option.is-active .dsel-diya {
    transform: scale(1.25);
    box-shadow: 0 0 10px 3px rgba(251, 191, 36, 0.65);
  }
  .dsel-option-text {
    position: relative;
    z-index: 1;
    flex: 1 1 auto;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .dsel-tick {
    position: relative;
    z-index: 1;
    flex: none;
    font-size: 0.8rem;
    color: #f59e0b;
    opacity: 0;
    transform: scale(0.4) rotate(-90deg);
    transition: opacity 0.25s ease, transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  .dsel-option.is-selected .dsel-tick {
    opacity: 1;
    transform: scale(1) rotate(0deg);
    animation: dselTwinkle 2s ease-in-out infinite;
  }
  .dsel-option.is-selected {
    font-weight: 700;
    color: #7c2d12;
  }
  .dsel-option.is-selected.is-active { color: #fde68a; }

  .dsel-menu::-webkit-scrollbar { width: 6px; }
  .dsel-menu::-webkit-scrollbar-thumb {
    border-radius: 6px;
    background: linear-gradient(180deg, #fbbf24, #b45309);
  }

  @keyframes dselMenuIn {
    from { opacity: 0; transform: translateY(-10px) scaleY(0.86); filter: blur(3px); }
    to   { opacity: 1; transform: translateY(0) scaleY(1); filter: blur(0); }
  }
  @keyframes dselOptionIn {
    from { opacity: 0; transform: translateX(-12px); }
    to   { opacity: 1; transform: translateX(0); }
  }
  @keyframes dselThread {
    to { background-position: 200% 0; }
  }
  @keyframes dselSheen {
    0%, 62%  { left: -60%; }
    100%     { left: 130%; }
  }
  @keyframes dselTwinkle {
    0%, 100% { opacity: 1; transform: scale(1) rotate(0deg); }
    50%      { opacity: 0.65; transform: scale(1.25) rotate(90deg); }
  }
  @media (prefers-reduced-motion: reduce) {
    .dsel-trigger::before,
    .dsel-menu::before,
    .dsel-option.is-selected .dsel-tick { animation: none; }
    .dsel-menu, .dsel-option { animation-duration: 0.01ms; }
  }

  /* Product cards — horizontal: image left, details right */
  .product-grid-card {
    border-radius: 20px;
    overflow: hidden;
    background: #fff;
    border: 2px solid transparent !important;
    /* !important so it beats Bootstrap's .card { flex-direction: column } */
    flex-direction: row !important;
    transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
  }
  .product-grid-card:hover {
    transform: translateY(-8px);
    border-color: rgba(234, 88, 12, 0.35) !important;
    box-shadow: 0 24px 44px rgba(194, 65, 12, 0.22) !important;
  }
  /* Body must be allowed to shrink so long names/descriptions can ellipsis */
  .product-grid-card > .card-body {
    min-width: 0;
    padding: 18px 20px;
  }

  .product-grid-img-wrap {
    position: relative;
    overflow: hidden;
    flex: 0 0 40%;
    max-width: 260px;
    min-height: 250px;
    /* keeps the image square-ish with the card corners on the left edge */
    border-radius: 18px 0 0 18px;
  }
  .product-grid-img {
    position: absolute;
    inset: 0;
    height: 100%;
    width: 100%;
    object-fit: cover;
    transition: transform 0.45s ease;
  }
  .product-grid-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(to top, rgba(17,24,39,0.55), rgba(17,24,39,0));
    opacity: 0;
    transition: opacity 0.35s ease;
  }
  .product-grid-card:hover .product-grid-overlay {
    opacity: 1;
  }
  .product-grid-quick-btn {
    background: rgba(255,255,255,0.95);
    color: #111827;
    border: none;
    font-weight: 700;
    font-size: 0.85rem;
    padding: 9px 18px;
    border-radius: 50px;
    cursor: pointer;
    transform: translateY(12px);
    transition: transform 0.35s ease, background 0.2s ease;
    box-shadow: 0 8px 20px rgba(0,0,0,0.25);
  }
  .product-grid-card:hover .product-grid-quick-btn {
    transform: translateY(0);
  }
  .product-grid-quick-btn:hover {
    background: #fbbf24;
  }
  .product-grid-name {
    font-size: 1.15rem;
    line-height: 1.3;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .product-grid-desc {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    min-height: 38px;
  }
  .product-grid-price {
    font-size: 1.35rem;
  }
  /* Actions sit side by side now that the card is wide */
  .product-row-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
  }
  .product-row-actions > .btn {
    flex: 1 1 150px;
  }

  .product-row-sale {
    position: absolute;
    top: 12px; left: 12px;
    z-index: 2;
    border-radius: 30px;
    padding: 6px 12px;
    background: linear-gradient(135deg, #dc2626, #ea580c) !important;
    color: #fff;
    font-weight: 700;
    box-shadow: 0 6px 14px rgba(234,88,12,0.4);
  }
  .product-row-oos {
    position: absolute;
    top: 12px; right: 12px;
    z-index: 2;
    border-radius: 30px;
    padding: 6px 12px;
    background: rgba(15,23,42,0.82) !important;
    color: #fff;
    font-weight: 600;
    backdrop-filter: blur(4px);
  }

  /* Category quick-filter chips */
  .pp-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 22px;
  }
  .pp-chip {
    border: 1px solid rgba(245, 158, 11, 0.4);
    background: rgba(255,255,255,0.7);
    color: #9a3412;
    font-weight: 600;
    font-size: 0.88rem;
    border-radius: 50px;
    padding: 7px 16px;
    cursor: pointer;
    transition: transform 0.2s ease, background 0.2s ease, box-shadow 0.2s ease, color 0.2s ease;
  }
  .pp-chip:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 18px rgba(234, 88, 12, 0.18);
  }
  .pp-chip.is-active {
    background: linear-gradient(135deg, #c2410c, #ea580c);
    border-color: transparent;
    color: #fff;
    box-shadow: 0 8px 18px rgba(234, 88, 12, 0.35);
  }

  /* Loading skeletons */
  .pp-skel {
    background: linear-gradient(90deg, rgba(148,163,184,0.14) 25%, rgba(255,255,255,0.6) 37%, rgba(148,163,184,0.14) 63%);
    background-size: 400% 100%;
    animation: ppShimmer 1.4s ease infinite;
    border-radius: 10px;
  }
  .pp-skel-img {
    flex: 0 0 40%;
    max-width: 260px;
    min-height: 250px;
    border-radius: 18px 0 0 18px;
  }
  .pp-skel-line { height: 14px; width: 100%; }
  .pp-skel-line.short { height: 18px; width: 40%; }
  .pp-skel-btn { height: 40px; border-radius: 12px; }
  @keyframes ppShimmer {
    0% { background-position: 100% 0; }
    100% { background-position: -100% 0; }
  }

  /* Horizontal cards stay horizontal on smaller screens — just tighter */
  @media (max-width: 991.98px) {
    .product-grid-img-wrap,
    .pp-skel-img { flex-basis: 38%; min-height: 220px; }
    .product-grid-card > .card-body { padding: 16px; }
    .product-row-actions > .btn { flex: 1 1 100%; }
  }
  @media (max-width: 575.98px) {
    .product-grid-img-wrap,
    .pp-skel-img { flex-basis: 42%; min-height: 190px; max-width: 170px; }
    .product-grid-card > .card-body { padding: 12px 14px; }
    .product-grid-name { font-size: 1rem; }
    .product-grid-desc { -webkit-line-clamp: 2; min-height: 0; }
    .product-grid-price { font-size: 1.15rem; }
    .product-row-sale,
    .product-row-oos {
      top: 8px; left: 8px; right: auto;
      padding: 4px 8px;
      font-size: 0.62rem;
    }
    .product-row-oos { top: auto; bottom: 8px; }
    .product-grid-quick-btn { padding: 7px 12px; font-size: 0.75rem; }
  }
  @media (max-width: 575.98px) {
    .ph-smoke { display: none; }
    .ph-lotus { transform: scale(0.75); }
    .ph-lamp-left  { left: 4%; }
    .ph-lamp-right { right: 4%; }
    .ph-lamp-body { font-size: 1.2rem; }
  }

  @media (prefers-reduced-motion: reduce) {
    .pp-skel,
    .products-hero-aurora,
    .ph-rotate-track,
    .ph-mote,
    .ph-om,
    .ph-petal,
    .ph-lotus-core,
    .ph-lamp,
    .ph-lamp-flame,
    .explorer-card,
    .explorer-card::before,
    .explorer-icon,
    .explorer-count-num,
    .filter-icon { animation: none; }
    .ph-motes, .ph-smoke { display: none; }
    /* Freeze the rotator on the first word */
    .ph-rotate-track { transform: translateY(0); }
  }
  .product-row-stars {
    letter-spacing: 1px;
    font-size: 0.85rem;
    transform-origin: left center;
    transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  .product-grid-card:hover .product-row-stars { transform: scale(1.08); }

  /* ===== Card motion ===== */

  /* Cards rise into place on mount, staggered by index (delay is inline) */
  .pp-card-in {
    animation: ppCardIn 0.62s cubic-bezier(0.22, 1, 0.36, 1) both;
  }
  @keyframes ppCardIn {
    from { opacity: 0; transform: translateY(28px) scale(0.96); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }

  /* Gold bar that unrolls along the bottom edge on hover, with a light
     travelling through it once it is open */
  .pp-card-bar {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 4px;
    z-index: 4;
    pointer-events: none;
    background: linear-gradient(90deg, #7c2d12, #ea580c 35%, #fbbf24 65%, #fde68a);
    background-size: 220% 100%;
    transform: scaleX(0);
    transform-origin: left center;
    transition: transform 0.45s cubic-bezier(0.22, 1, 0.36, 1);
  }
  .product-grid-card:hover .pp-card-bar {
    transform: scaleX(1);
    animation: ppBarFlow 2.4s linear 0.45s infinite;
  }
  @keyframes ppBarFlow {
    to { background-position: 220% 0; }
  }

  .product-grid-card:hover .product-grid-img {
    transform: scale(1.09) rotate(1.2deg);
  }
  /* Title steps forward and warms up with the card */
  .product-grid-name {
    transition: transform 0.32s cubic-bezier(0.34, 1.56, 0.64, 1), color 0.3s ease;
  }
  .product-grid-card:hover .product-grid-name {
    transform: translateX(5px);
    color: #c2410c;
  }
  .product-grid-card:hover .product-grid-price {
    transform: scale(1.09);
  }
  .product-grid-price {
    display: inline-block;
    transform-origin: left center;
    transition: transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
  }

  /* Badges keep a slow life of their own */
  .product-row-sale {
    animation: ppSaleThrob 2.2s ease-in-out infinite;
  }
  @keyframes ppSaleThrob {
    0%, 100% { transform: scale(1);    box-shadow: 0 6px 14px rgba(234,88,12,0.4); }
    50%      { transform: scale(1.06); box-shadow: 0 8px 24px rgba(234,88,12,0.75); }
  }
  .pp-stock-dot {
    display: inline-block;
    animation: ppStockPulse 1.9s ease-in-out infinite;
  }
  @keyframes ppStockPulse {
    0%, 100% { opacity: 0.45; transform: scale(0.85); }
    50%      { opacity: 1;    transform: scale(1.25); }
  }

  /* Quick View pulses invitingly while the card is hovered */
  .product-grid-card:hover .product-grid-quick-btn {
    animation: ppQuickGlow 2s ease-in-out 0.35s infinite;
  }
  @keyframes ppQuickGlow {
    0%, 100% { box-shadow: 0 8px 20px rgba(0,0,0,0.25); }
    50%      { box-shadow: 0 8px 28px rgba(251,191,36,0.85); }
  }

  /* ===== Buttons ===== */
  .pp-btn {
    position: relative;
    overflow: hidden;
    /* own stacking context so ::before can sit above the background,
       below the label */
    isolation: isolate;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    border-radius: 12px;
    transition: transform 0.28s cubic-bezier(0.34, 1.56, 0.64, 1),
      box-shadow 0.28s ease, background 0.25s ease, color 0.2s ease,
      border-color 0.2s ease;
  }
  /* Colour wipes in from the left instead of a light band sweeping over */
  .pp-btn::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: -1;
    border-radius: inherit;
    background: linear-gradient(120deg, #7c2d12, #ea580c 55%, #f59e0b);
    transform: scaleX(0);
    transform-origin: left center;
    transition: transform 0.42s cubic-bezier(0.22, 1, 0.36, 1);
    pointer-events: none;
  }
  .pp-btn:hover::before { transform: scaleX(1); }
  .pp-btn:hover  { transform: translateY(-3px); }
  .pp-btn:active { transform: translateY(-1px) scale(0.97); }
  .pp-btn-label  { position: relative; }

  .product-row-btn:hover {
    box-shadow: 0 12px 26px rgba(194, 65, 12, 0.42);
  }
  .prb-arrow {
    display: inline-block;
    transition: transform 0.32s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  .product-row-btn:hover .prb-arrow { transform: translateX(7px); }

  /* ===== Add to Wishlist =====
     The emotional button on the page, so it carries more than the shared
     .pp-btn treatment: a rose rim that is a gradient rather than a flat
     border, a heart that beats even at rest, and a burst when it lands. */
  .product-wishlist-btn {
    position: relative;
    overflow: visible;
    border: 2px solid transparent !important;
    background:
      linear-gradient(#fff, #fff) padding-box,
      linear-gradient(120deg, #9f1239, #f43f5e 45%, #fb7185 70%, #9f1239) border-box !important;
    background-size: 100% 100%, 220% 100% !important;
    color: #9f1239 !important;
    animation: ppWishRim 7s linear infinite;
  }
  @keyframes ppWishRim {
    from { background-position: 0 0, 0% 50%; }
    to   { background-position: 0 0, 220% 50%; }
  }
  .product-wishlist-btn::before {
    background: linear-gradient(120deg, #9f1239, #dc2626 55%, #f43f5e);
  }
  .product-wishlist-btn:hover {
    color: #fff1f2 !important;
    box-shadow: 0 14px 30px rgba(190, 18, 60, 0.42);
  }

  /* A soft rose pulse sitting behind the button, so it reads as the warm
     action next to the plain one beside it */
  .pwb-ring {
    position: absolute;
    inset: -3px;
    z-index: -2;
    border-radius: inherit;
    pointer-events: none;
    background: radial-gradient(60% 120% at 50% 50%, rgba(244, 63, 94, 0.35), transparent 70%);
    opacity: 0;
    animation: ppWishGlow 3.2s ease-in-out infinite;
  }
  @keyframes ppWishGlow {
    0%, 100% { opacity: 0.25; transform: scale(0.94); }
    50%      { opacity: 0.7;  transform: scale(1.06); }
  }

  /* The heart has a pulse of its own at rest, and races on hover */
  .pwb-heart {
    display: inline-block;
    position: relative;
    animation: ppHeartIdle 2.6s ease-in-out infinite;
  }
  @keyframes ppHeartIdle {
    0%, 82%, 100% { transform: scale(1); }
    88%           { transform: scale(1.16); }
    94%           { transform: scale(1.02); }
  }
  .product-wishlist-btn:hover .pwb-heart {
    animation: ppHeartbeat 0.95s ease-in-out infinite;
  }
  @keyframes ppHeartbeat {
    0%, 100% { transform: scale(1); }
    22%      { transform: scale(1.32); }
    38%      { transform: scale(1.04); }
    58%      { transform: scale(1.24); }
  }

  /* ---- the moment it lands ---- */
  .product-wishlist-btn.is-bursting {
    animation: ppWishRim 7s linear infinite, ppWishPop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  @keyframes ppWishPop {
    0%   { transform: scale(1); }
    35%  { transform: scale(0.94); }
    70%  { transform: scale(1.06); }
    100% { transform: scale(1); }
  }
  /* the ring snaps outward once rather than continuing to breathe */
  .product-wishlist-btn.is-bursting .pwb-ring {
    animation: ppWishRingBurst 0.65s cubic-bezier(0.22, 1, 0.36, 1);
  }
  @keyframes ppWishRingBurst {
    0%   { opacity: 0.95; transform: scale(0.9); }
    100% { opacity: 0;    transform: scale(1.9); }
  }
  .product-wishlist-btn.is-bursting .pwb-heart {
    animation: ppHeartLaunch 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  @keyframes ppHeartLaunch {
    0%   { transform: scale(1); }
    30%  { transform: scale(1.55) rotate(-8deg); }
    100% { transform: scale(1) rotate(0deg); }
  }

  /* Hearts fly out from the button's centre along their own angle. The layer
     has no size of its own so the origin stays put whatever the label says. */
  .pwb-burst {
    position: absolute;
    left: 50%;
    top: 50%;
    width: 0;
    height: 0;
    z-index: 4;
    pointer-events: none;
  }
  .pwb-burst-heart {
    position: absolute;
    left: 0;
    top: 0;
    font-size: 1rem;
    line-height: 1;
    opacity: 0;
    will-change: transform, opacity;
    animation: ppBurstFly 0.9s cubic-bezier(0.18, 0.7, 0.3, 1) forwards;
  }
  @keyframes ppBurstFly {
    0%   { opacity: 0; transform: translate(-50%, -50%) scale(0.2); }
    18%  { opacity: 1; transform: translate(calc(-50% + var(--bx) * 0.32), calc(-50% + var(--by) * 0.32)) scale(var(--bs)); }
    100% { opacity: 0; transform: translate(calc(-50% + var(--bx)), calc(-50% + var(--by) - 16px)) scale(calc(var(--bs) * 0.55)); }
  }

  .product-edit-btn::before {
    background: linear-gradient(120deg, #b45309, #f59e0b 55%, #fbbf24);
  }
  .product-edit-btn:hover {
    box-shadow: 0 12px 26px rgba(245, 158, 11, 0.42);
    color: #431407;
  }
  .peb-pencil {
    display: inline-block;
    transform-origin: 80% 80%;
  }
  .product-edit-btn:hover .peb-pencil {
    animation: ppWiggle 0.62s ease-in-out infinite;
  }
  @keyframes ppWiggle {
    0%, 100% { transform: rotate(0deg); }
    32%      { transform: rotate(-17deg) translateX(-1px); }
    66%      { transform: rotate(13deg)  translateX(1px); }
  }

  /* Chips fill from the bottom up */
  .pp-chip { position: relative; overflow: hidden; isolation: isolate; }
  .pp-chip::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: -1;
    background: linear-gradient(135deg, #c2410c, #ea580c);
    transform: scaleY(0);
    transform-origin: bottom center;
    transition: transform 0.32s cubic-bezier(0.22, 1, 0.36, 1);
    pointer-events: none;
  }
  .pp-chip:hover::before { transform: scaleY(1); }
  .pp-chip:hover { color: #fff; }
  .pp-chip:active { transform: scale(0.95); }
  .pp-chip.is-active { animation: ppChipPop 0.45s cubic-bezier(0.34, 1.56, 0.64, 1); }
  @keyframes ppChipPop {
    0%   { transform: scale(0.86); }
    55%  { transform: scale(1.09); }
    100% { transform: scale(1); }
  }

  /* Search button: its own gradient slides back and forth, always */
  .products-search-btn {
    background: linear-gradient(120deg, #9a3412, #ea580c 40%, #f59e0b 65%, #ea580c) !important;
    background-size: 260% 100% !important;
    animation: ppSearchFlow 6s ease-in-out infinite alternate;
  }
  @keyframes ppSearchFlow {
    from { background-position: 0% 50%; }
    to   { background-position: 100% 50%; }
  }
  /* the !important above also wins on :hover, so speed the flow up instead */
  .products-search-btn:hover { animation-duration: 1.8s; }
  .products-search-btn:active { transform: translateY(0) scale(0.97); }

  @media (prefers-reduced-motion: reduce) {
    .pp-card-in,
    .product-row-sale,
    .pp-stock-dot,
    .product-grid-card:hover .product-grid-quick-btn,
    .product-wishlist-btn:hover .pwb-heart,
    .product-wishlist-btn,
    .product-wishlist-btn.is-bursting,
    .product-wishlist-btn.is-bursting .pwb-heart,
    .pwb-heart,
    .pwb-ring,
    .pwb-burst-heart,
    .product-edit-btn:hover .peb-pencil,
    .product-grid-card:hover .pp-card-bar,
    .products-search-btn,
    .pp-chip.is-active { animation: none !important; }
    .product-grid-card:hover { transform: none; }
    .product-grid-card:hover .product-grid-img { transform: none; }
    .product-grid-card:hover .product-grid-name { transform: none; }
    /* the celebration is the motion, so with motion off it does not play —
       the toast still confirms the item was added */
    .pwb-burst { display: none; }
    .pwb-ring { opacity: 0.4; transform: none; }
  }
`;

export default ProductsPage;
